// src/components/GuidedJourneyModal.jsx
// Step-by-step guided journey modal for AI-assisted idea development
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, RotateCcw, Save, Check, Loader2, AlertCircle } from 'lucide-react';
import { enhanceIdeaStep, ENHANCEMENT_DENIAL_REFRESH_CODES } from '../utils/enhanceIdea';
import { auth } from '../firebase';

// Step configuration
const STEPS = [
  { number: 1, title: 'Title & Description', fields: ['title', 'description'] },
  { number: 2, title: 'Tags & Subtasks', fields: ['tags', 'subtasks'] },
  { number: 3, title: 'Plan Steps', fields: ['planSteps'] }
];

function GuidedJourneyModal({
  isOpen,
  onClose,
  originalDescription,
  existingTitle = '',
  onComplete,
  onSavePartial,
  /** Optional US-04: show when snapshot says user may not have enhancement (stale Path Selector) */
  entitlementBlockedHint = null,
}) {
  // Current step (1, 2, or 3)
  const [currentStep, setCurrentStep] = useState(1);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Feedback for regeneration
  const [feedback, setFeedback] = useState('');
  
  // Approved data from previous steps
  const [approvedData, setApprovedData] = useState({});
  
  // Current step's generated data (editable)
  const [currentData, setCurrentData] = useState({});
  
  // For tags/subtasks editing
  const [newTag, setNewTag] = useState('');
  const [newSubtask, setNewSubtask] = useState('');
  
  // Track if we've initialized for this session
  const hasInitialized = useRef(false);
  const lastDescription = useRef('');

  // Generate content for current step
  const generateStep = useCallback(async (stepNum, feedbackText = '') => {
    // Guard: Don't proceed if no description
    if (!originalDescription || originalDescription.trim().length === 0) {
      console.error('[GuidedJourneyModal] No description available');
      setError('No description provided. Please close and try again.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Build context from approved data
      const context = {
        title: approvedData.title || existingTitle,
        description: approvedData.description || originalDescription,
        tags: approvedData.tags || [],
        subtasks: approvedData.subtasks || []
      };

      const result = await enhanceIdeaStep(
        originalDescription,
        stepNum,
        context,
        feedbackText,
        existingTitle
      );

      setCurrentData(result);
      setFeedback('');
    } catch (err) {
      console.error('[GuidedJourneyModal] Error generating step:', err);
      if (err?.code && ENHANCEMENT_DENIAL_REFRESH_CODES.has(err.code) && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (_) {
          /* ignore */
        }
      }
      setError(err.message || 'Failed to generate content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [approvedData, originalDescription, existingTitle]);

  // Reset state and trigger initial generation when modal opens
  useEffect(() => {
    if (isOpen && originalDescription && originalDescription.trim().length > 0) {
      // Check if this is a new session (new description or first open)
      if (!hasInitialized.current || lastDescription.current !== originalDescription) {
        hasInitialized.current = true;
        lastDescription.current = originalDescription;
        
        // Reset all state
        setCurrentStep(1);
        setApprovedData({});
        setCurrentData({});
        setFeedback('');
        setError(null);
        
        // Generate step 1 content
        generateStep(1);
      }
    }
    
    // Reset initialization flag when modal closes
    if (!isOpen) {
      hasInitialized.current = false;
      lastDescription.current = '';
    }
  }, [isOpen, originalDescription, generateStep]);

  // Generate content when moving to a new step (after step 1)
  useEffect(() => {
    // Only auto-generate for steps 2 and 3 after initialization
    if (isOpen && hasInitialized.current && currentStep > 1 && !currentData[STEPS[currentStep - 1]?.fields[0]]) {
      generateStep(currentStep);
    }
  }, [isOpen, currentStep, generateStep, currentData]);

  if (!isOpen) return null;

  // Handlers
  const handleRegenerate = () => {
    generateStep(currentStep, feedback);
  };

  const handleAcceptAndContinue = () => {
    // Merge current data into approved data
    const newApprovedData = { ...approvedData, ...currentData };
    setApprovedData(newApprovedData);
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      setCurrentData({});
      setFeedback('');
    } else {
      // Final step - complete the journey
      handleComplete(newApprovedData);
    }
  };

  const handleComplete = (finalData) => {
    onComplete({
      title: finalData.title,
      description: finalData.description,
      tags: finalData.tags || [],
      subtasks: finalData.subtasks || [],
      planSteps: finalData.planSteps || {}
    });
    onClose();
  };

  const handleSaveAndExit = () => {
    // Save partial progress as raw idea
    const partialData = {
      title: approvedData.title || currentData.title || generateTitleFromDescription(originalDescription),
      description: approvedData.description || currentData.description || originalDescription,
      tags: approvedData.tags || currentData.tags || [],
      subtasks: approvedData.subtasks || currentData.subtasks || []
    };
    onSavePartial(partialData);
    onClose();
  };

  const generateTitleFromDescription = (desc) => {
    // Generate simple title from first 50 chars of description
    const truncated = desc.trim().substring(0, 50);
    return truncated.length < desc.length ? `${truncated}...` : truncated;
  };

  // Tag handlers
  const handleAddTag = () => {
    if (newTag.trim() && !currentData.tags?.includes(newTag.trim())) {
      setCurrentData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setCurrentData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  // Subtask handlers
  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      setCurrentData(prev => ({
        ...prev,
        subtasks: [...(prev.subtasks || []), { task: newSubtask.trim(), completed: false }]
      }));
      setNewSubtask('');
    }
  };

  const handleRemoveSubtask = (index) => {
    setCurrentData(prev => ({
      ...prev,
      subtasks: (prev.subtasks || []).filter((_, i) => i !== index)
    }));
  };

  // Render step content
  const renderStepContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            Generating {STEPS[currentStep - 1].title.toLowerCase()}...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-600 dark:text-red-400 text-center mb-4">{error}</p>
          <button
            onClick={() => generateStep(currentStep)}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  // Step 1: Title + Description
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Original Description */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Your Original Idea
        </label>
        <p className="text-sm text-gray-700 dark:text-gray-300">{originalDescription}</p>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Suggested Title
        </label>
        <input
          type="text"
          value={currentData.title || ''}
          onChange={(e) => setCurrentData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Idea title"
        />
      </div>

      {/* Enhanced Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Enhanced Description
        </label>
        <textarea
          value={currentData.description || ''}
          onChange={(e) => setCurrentData(prev => ({ ...prev, description: e.target.value }))}
          rows={8}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
          placeholder="Enhanced description"
        />
      </div>
    </div>
  );

  // Step 2: Tags + Subtasks
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Approved Summary */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Approved from Step 1
        </label>
        <p className="font-semibold text-gray-900 dark:text-white">{approvedData.title}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
          {approvedData.description?.substring(0, 150)}...
        </p>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tags
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {(currentData.tags || []).map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary"
            >
              {tag}
              <button
                onClick={() => handleRemoveTag(tag)}
                className="hover:bg-primary/30 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Add tag"
          />
          <button
            onClick={handleAddTag}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      {/* Subtasks */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Subtasks / To-dos
        </label>
        {(currentData.subtasks || []).length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden mb-3 max-h-60 overflow-y-auto">
            <ul className="divide-y divide-gray-200 dark:divide-gray-600">
              {(currentData.subtasks || []).map((subtask, index) => {
                const taskText = typeof subtask === 'string' ? subtask : subtask.task;
                return (
                  <li key={index} className="flex items-center gap-2 px-3 py-2">
                    <span className="text-gray-700 dark:text-gray-300 flex-1">{taskText}</span>
                    <button
                      onClick={() => handleRemoveSubtask(index)}
                      className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newSubtask}
            onChange={(e) => setNewSubtask(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubtask())}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Add subtask"
          />
          <button
            onClick={handleAddSubtask}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  // Step 3: Plan Steps
  const renderStep3 = () => (
    <div className="space-y-6">
      {/* Approved Summary */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          Building plan for
        </label>
        <p className="font-semibold text-gray-900 dark:text-white">{approvedData.title}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {(approvedData.tags || []).map((tag, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Plan Steps */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Generated Plan Steps
        </label>
        <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4 max-h-80 overflow-y-auto">
          {currentData.planSteps && Object.keys(currentData.planSteps).length > 0 ? (
            Object.entries(currentData.planSteps).map(([category, steps]) => (
              <div key={category}>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{category}</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  {Array.isArray(steps) && steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              No plan steps generated yet.
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          These steps will be appended to your description when you finish.
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" 
        onClick={handleSaveAndExit}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Guided Journey
              </h2>
              {/* Step Indicator */}
              <div className="flex items-center gap-2 mt-2">
                {STEPS.map((step, index) => (
                  <React.Fragment key={step.number}>
                    <div 
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                        step.number < currentStep 
                          ? 'bg-green-500 text-white' 
                          : step.number === currentStep 
                            ? 'bg-primary text-white' 
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {step.number < currentStep ? <Check className="w-4 h-4" /> : step.number}
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className={`w-8 h-0.5 ${
                        step.number < currentStep 
                          ? 'bg-green-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Step {currentStep}: {STEPS[currentStep - 1].title}
              </p>
            </div>
            <button
              onClick={handleSaveAndExit}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Save and exit"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {entitlementBlockedHint && (
            <div className="px-4 sm:px-6 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              <p className="text-xs text-amber-800 dark:text-amber-200">{entitlementBlockedHint}</p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {renderStepContent()}
          </div>

          {/* Feedback Section (when not loading/error) */}
          {!isLoading && !error && (
            <div className="px-4 sm:px-6 pb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Optional: Tell AI what to change..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleRegenerate}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Regenerate</span>
                </button>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSaveAndExit}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              Save & Exit
            </button>
            
            <button
              onClick={handleAcceptAndContinue}
              disabled={isLoading || error}
              className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep < 3 ? (
                <>
                  Accept & Continue
                  <ChevronRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Accept & Finish
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default GuidedJourneyModal;
